import type {CarouselItemData} from '@Components/carousel/carousel.types';
import type {StockDropPosition} from '@Panels/stock-panel/stock-drag-controller';
import type {GridItemStorage} from '@Components/base-grid/components/grid-item';
import {GRID_ITEM_TYPE} from '@Components/base-grid/components/grid-item';
import type {GridSkeletonItemStorage} from '@Components/base-grid/components/grid-skeleton-item';
import {isChristmasSeason, isHalloweenSeason} from '@Utils/seasonal-promotion.util';
import {isPremiumUser} from '@Utils/user.util';
import type {LazyLoadingGetterParams, StockBackendData, StockGetterOptions, StockSources} from '@Libraries/pmw-stock-media-library';
import type {ItemData} from '@PosterWhiteboard/page/add-item.class';
import {PosterEditorTestId} from '@/test-ids/poster-editor-test-id';

export const IS_GETTY_ENABLED = true;

export enum StockPanelTabs {
  ALL = 'ALL',
  PHOTOS = 'PHOTOS',
  SHAPES = 'SHAPES',
  STICKERS = 'STICKERS',
  ICONS = 'ICONS',
  VIDEOS = 'VIDEOS',
  AUDIOS = 'AUDIO',
  TEXT_TO_IMAGE = 'TEXT_TO_IMAGE',
}

export const STOCK_PANEL_TAB_ID = 'STOCK_PANEL_TAB';

export const STOCK_PANEL_AI_IMAGE_PANEL_ID = 'STOCK_PANEL_AI_IMAGE_PANEL';
export const STOCK_PANEL_AI_VIDEO_PANEL_ID = 'STOCK_PANEL_AI_VIDEO_PANEL';
export const STOCK_PANEL_AI_VOICE_PANEL_ID = 'STOCK_PANEL_AI_VOICE_PANEL';

export const getAiSkeletonItem = (id: string): GridSkeletonItemStorage => {
  return {
    id: id,
    type: GRID_ITEM_TYPE.SKELETON,
    width: 180,
    height: 70,
    useFixedDimensions: false,
  };
};

export const MIN_STOCK_ITEMS_TO_DISPLAY = 3;
export const SCROLL_SHADOW_THRESHOLD = 3;

export enum StockButtonFilters {
  PRICING = 'Pricing',
  ANIMATION = 'Animation',
  QUALITY = 'Quality',
  GENRE = 'Genre',
  INSTRUMENT = 'Instrument',
  MOOD = 'Mood',
  SFX = 'Effects',
  TYPE = 'Type',
  CATEGORY = 'Category',
  SEASONAL = 'Seasonal',
  ORIENTATION = 'Orientation',
}

export enum OrientationSubFilter {
  LANDSCAPE = 'landscape',
  PORTRAIT = 'portrait',
  SQUARE = 'square',
}

export enum PricingSubFilter {
  FREE = '1',
  PREMIUM = '2',
}

export enum AnimationSubFilter {
  ANIMATED = '3',
  STILL = '4',
}

export enum QualitySubFilter {
  UHD = '5',
}

export const ICON_FILTERS_TO_CATEGORY_MAPPING: Record<string, string[]> = {
  colored: ['lineal', 'colored_fill'],
  fill: ['black_and_white_fill', 'colored_fill'],
  outline: ['outline'],
  hand_drawn: ['hand_drawn'],
};

export const SHAPE_FILTERS_TO_CATEGORY_MAPPING: Record<string, string[]> = {
  lines: ['line', 'divider', 'stroke', 'arrow'],
  basic: ['basic', 'abstract', '3d'],
  decorative: ['decorative', 'frame', 'paint', 'stroke'],
  labels: ['ribbon', 'label'],
};

export enum StockGridIDs {
  PHOTOS = 'photo-items-grid',
  VIDEOS = 'video-items-grid',
  AUDIOS = 'audio-items-grid',
  SHAPES = 'shapes-items-grid',
  ICONS = 'icons-items-grid',
  STICKERS = 'stickers-items-grid',
  TEXT_TO_IMAGE = 'text-to-image-stock-grid',
}

export enum StockCarouselIDs {
  RECENTS = 'recents-carousel',
  PHOTOS = 'photos-carousel',
  STICKERS = 'stickers-carousel',
  SHAPES = 'shapes-carousel',
  VIDEOS = 'videos-carousel',
  AUDIOS = 'audios-carousel',
  ICONS = 'icons-carousel',
  AI = 'ai-carousel',
}

/**
 * The kinds of AI-generated content the aggregated "AI" carousel can show. Each open point of the stock panel
 * enables the subset that makes sense for it (e.g. replace-image shows images + videos but not audio).
 * IMAGES covers AI stickers too — generated stickers come back from the same getter as AI images and carry no
 * media-type flag, so they are one indistinguishable bucket.
 */
export enum AiCarouselContentType {
  IMAGES = 'IMAGES',
  VIDEOS = 'VIDEOS',
  AUDIO = 'AUDIO',
}

export const AI_CAROUSEL_CONTENT_TYPES_ALL = [AiCarouselContentType.IMAGES, AiCarouselContentType.VIDEOS, AiCarouselContentType.AUDIO];

export interface AiCarouselItemMeta {
  aiContentType: AiCarouselContentType;
  aiId: number;
}

export const AI_CREATE_WITH_AI_BUTTON_ID = 'ai-create-with-ai';
export const AI_CREATE_WITH_AI_BUTTON_WIDTH = 160;

export const CAROUSEL_ITEM_HEIGHT = 80;
// Caps a carousel tile at 2:1 (160px at the 80px row height) so an extreme-aspect item (a long thin sticker or
// shape) can't fill the whole visible row; the media letterboxes inside the capped tile like it does in the grid.
export const MAX_CAROUSEL_ITEM_ASPECT_RATIO = 2;

export const STOCK_GRID_ITEM_DIMENSION = 80;

export enum InputFieldIconID {
  CANCEL = 'CANCEL',
  SEARCH = 'SEARCH',
}

export const AUDIO_CATEGORY_ID_TO_STOCK_BUTTON_FILTER: Record<string, StockButtonFilters> = {
  genre: StockButtonFilters.GENRE,
  instrument: StockButtonFilters.INSTRUMENT,
  mood: StockButtonFilters.MOOD,
  sfx: StockButtonFilters.SFX,
};

export enum StockCarouselType {
  RECENTS = 'Recents',
  PHOTOS = 'Photos',
  STICKERS = 'Stickers',
  SHAPES = 'Shapes',
  VIDEOS = 'Videos',
  AUDIOS = 'Audio',
  ICONS = 'Icons',
  AI = 'AI',
}

export const STOCK_TABS_TO_CAROUSEL_TYPE_MAPPING: Record<StockPanelTabs, StockCarouselType[] | undefined> = {
  // ALL is the browse selector itself, not a carousel row (Recents is added separately in getCarouselsForTabs).
  [StockPanelTabs.ALL]: undefined,
  [StockPanelTabs.PHOTOS]: [StockCarouselType.PHOTOS],
  [StockPanelTabs.SHAPES]: [StockCarouselType.SHAPES],
  [StockPanelTabs.STICKERS]: [StockCarouselType.STICKERS],
  [StockPanelTabs.ICONS]: [StockCarouselType.ICONS],
  [StockPanelTabs.VIDEOS]: [StockCarouselType.VIDEOS],
  [StockPanelTabs.AUDIOS]: [StockCarouselType.AUDIOS],
  // The TEXT_TO_IMAGE tab is grid-only: AI images surface in the aggregated AI carousel, not a per-tab row.
  [StockPanelTabs.TEXT_TO_IMAGE]: undefined,
};

export const STOCK_CAROUSEL_TYPE_TO_ID: Record<StockCarouselType, StockCarouselIDs> = {
  [StockCarouselType.RECENTS]: StockCarouselIDs.RECENTS,
  [StockCarouselType.PHOTOS]: StockCarouselIDs.PHOTOS,
  [StockCarouselType.STICKERS]: StockCarouselIDs.STICKERS,
  [StockCarouselType.SHAPES]: StockCarouselIDs.SHAPES,
  [StockCarouselType.VIDEOS]: StockCarouselIDs.VIDEOS,
  [StockCarouselType.AUDIOS]: StockCarouselIDs.AUDIOS,
  [StockCarouselType.ICONS]: StockCarouselIDs.ICONS,
  [StockCarouselType.AI]: StockCarouselIDs.AI,
};

/** The grid tab a carousel's "View all" drills into. AI opens the AI panel and Recents has no View-all, so both are absent. */
export const STOCK_CAROUSEL_TYPE_TO_TAB: Partial<Record<StockCarouselType, StockPanelTabs>> = {
  [StockCarouselType.PHOTOS]: StockPanelTabs.PHOTOS,
  [StockCarouselType.STICKERS]: StockPanelTabs.STICKERS,
  [StockCarouselType.SHAPES]: StockPanelTabs.SHAPES,
  [StockCarouselType.VIDEOS]: StockPanelTabs.VIDEOS,
  [StockCarouselType.AUDIOS]: StockPanelTabs.AUDIOS,
  [StockCarouselType.ICONS]: StockPanelTabs.ICONS,
};

export const AudioFilters = [StockButtonFilters.GENRE, StockButtonFilters.INSTRUMENT, StockButtonFilters.MOOD, StockButtonFilters.SFX];

export interface CarouselData {
  [StockCarouselType.RECENTS]?: CarouselItemData[];
  [StockCarouselType.PHOTOS]?: CarouselItemData[];
  [StockCarouselType.STICKERS]?: CarouselItemData[];
  [StockCarouselType.SHAPES]?: CarouselItemData[];
  [StockCarouselType.VIDEOS]?: CarouselItemData[];
  [StockCarouselType.AUDIOS]?: CarouselItemData[];
  [StockCarouselType.ICONS]?: CarouselItemData[];
  [StockCarouselType.AI]?: CarouselItemData[];
}

export type StockFilterID = StockButtonFilters;

export type StockPrimaryCTA = (selectedItems: GridItemStorage[], dropPosition?: StockDropPosition) => Promise<void>;

export const STOCK_PANEL_ADD_LOADING_KEY = 'addStockMediaItem';

export type StockItemDelivery = (items: ItemData[]) => void | Promise<void>;

export interface UploadStockSelectionParams {
  /** The items being added. AI voice items upload sequentially first; everything else uploads in parallel. */
  items: GridItemStorage[];
  /** Key of the loading toast the pipeline opens and keeps the progress text on. The caller hides it. */
  loadingKey: string;
  /** Deliver the whole selection in one onDeliver call instead of streaming each item as its upload settles. */
  addInBulk: boolean;
  onDeliver: StockItemDelivery;
  shouldLoadDataUrlImage?: boolean;
  dropPosition?: StockDropPosition;
  /** Runs once, after the voice pre-pass and before the parallel uploads start. */
  onUploadsStarting?: (itemsToUpload: GridItemStorage[]) => void;
}

export interface StockSelectionUpload {
  item: GridItemStorage;
  uploadedItem: ItemData | null;
}

export interface StockSelectionUploadResult {
  /** One entry per item that went through the parallel upload phase, in selection order. */
  uploads: StockSelectionUpload[];
  uploadedItems: ItemData[];
}

// if, in future these filters are data driven, we'd need an id and a name. For now, i've set the both to IDs
export interface StockFilter {
  id: StockFilterID;
  type: StockFilterTypes;
  name: string;
  subFilters: StockButtonSubfilter[];
}

export type StockSubfilter = StockButtonSubfilter;

export interface StockButtonSubfilter {
  text: string;
  id: string;
  testId?: PosterEditorTestId;
}

export interface SelectedFilter {
  filter: string;
  subFilter: string | null;
}

export enum StockFilterTypes {
  BUTTON,
  DROPDOWN,
}

export const QUALITY_SUBFILTERS: StockButtonSubfilter[] = [
  {
    text: '4K',
    id: QualitySubFilter.UHD,
  },
];

export enum SeasonSearchSubFilterId {
  AI = 'ai',
  Halloween = 'halloween',
  Christmas = 'christmas',
}

export const DEFAULT_APPLIED_SUBFILTER: StockSubfilter = {
  text: 'All',
  id: '0',
};

export const UHD_APPLIED_VIDEO_FILTER_ID = QualitySubFilter.UHD;

/**
 * Every pricing sub-filter that exists, independent of who is asking. Callers that render chips for the
 * current user want {@link getPricingFilter} instead; this one also serves as the id-to-label dictionary
 * for surfaces that describe another user's logged filters (see manageapp-user-stock-searches), so it
 * must stay tier-agnostic — dropping an entry here would make those labels fall back to raw ids.
 */
export const getPricingSubFilter = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_free'),
      id: PricingSubFilter.FREE,
    },
    {
      text: window.i18next.t('pmwjs_getty_images'),
      id: PricingSubFilter.PREMIUM,
    },
  ];
};

const FREE_PRICING_SUBFILTER_ID: string = PricingSubFilter.FREE;

/** Subscribers have no free-vs-paid choice to make — Getty is covered by the plan — so the Free chip is dropped for them. */
const getPricingSubFiltersForCurrentUser = (): StockButtonSubfilter[] => {
  if (isPremiumUser()) {
    return getPricingSubFilter().filter((subFilter) => subFilter.id !== FREE_PRICING_SUBFILTER_ID);
  }
  return getPricingSubFilter();
};

export const getPricingFilter = (): StockFilter => {
  const pricingFilterName = window.i18next.t('pmwjs_stock_source');
  return {type: StockFilterTypes.BUTTON, name: pricingFilterName, subFilters: getPricingSubFiltersForCurrentUser(), id: StockButtonFilters.PRICING};
};

export const getAnimationSubFilters = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_animated'),
      id: AnimationSubFilter.ANIMATED,
    },
    {
      text: window.i18next.t('pmwjs_still'),
      id: AnimationSubFilter.STILL,
      testId: PosterEditorTestId.STICKER_STILL_FILTER,
    },
  ];
};

export const getOrientationSubFilters = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_landscape'),
      id: OrientationSubFilter.LANDSCAPE,
    },
    {
      text: window.i18next.t('pmwjs_portrait'),
      id: OrientationSubFilter.PORTRAIT,
    },
    {
      text: window.i18next.t('pmwjs_square'),
      id: OrientationSubFilter.SQUARE,
    },
  ];
};

const buildOrientationDropdown = (subFilters: StockButtonSubfilter[]): StockFilter => {
  return {type: StockFilterTypes.DROPDOWN, name: window.i18next.t('pmwjs_orientation'), subFilters, id: StockButtonFilters.ORIENTATION};
};

export const getOrientationFilter = (): StockFilter => {
  return buildOrientationDropdown(getOrientationSubFilters());
};

/** Video providers have no square orientation bucket, so Square is omitted for the videos tab. */
export const getOrientationFilterForVideos = (): StockFilter => {
  return buildOrientationDropdown(getOrientationSubFilters().filter((subFilter) => subFilter.id !== OrientationSubFilter.SQUARE.valueOf()));
};

export const getIconSubFilters = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_colored'),
      id: 'colored',
    },
    {
      text: window.i18next.t('pmwjs_fill'),
      id: 'fill',
    },
    {
      text: window.i18next.t('pmwjs_outline'),
      id: 'outline',
    },
    {
      text: window.i18next.t('pmwjs_hand_drawn'),
      id: 'hand_drawn',
    },
  ];
};

const getShapesSubFilters = (): StockButtonSubfilter[] => {
  return [
    {
      text: window.i18next.t('pmwjs_shapes_filter_lines'),
      id: 'lines',
    },
    {
      text: window.i18next.t('pmwjs_shapes_filter_basic'),
      id: 'basic',
    },
    {
      text: window.i18next.t('pmwjs_shapes_filter_decorative'),
      id: 'decorative',
    },
    {
      text: window.i18next.t('pmwjs_shapes_filter_labels'),
      id: 'labels',
    },
  ];
};

export const getSeasonalFilter = (): StockFilter[] => {
  const seasonalSubFilters = [];
  if (isHalloweenSeason()) {
    seasonalSubFilters.push({
      id: SeasonSearchSubFilterId.Halloween,
      text: window.i18next.t('pmwjs_halloween'),
    });
  }
  if (isChristmasSeason()) {
    seasonalSubFilters.push({
      id: SeasonSearchSubFilterId.Christmas,
      text: window.i18next.t('pmwjs_christmas'),
    });
  }
  if (seasonalSubFilters.length) {
    return [
      {
        id: StockButtonFilters.SEASONAL,
        type: StockFilterTypes.BUTTON,
        name: StockButtonFilters.SEASONAL,
        subFilters: seasonalSubFilters,
      },
    ];
  }
  return [];
};

export const getStockTabsToFilterMapping = (videoQualityFiltersDisabled?: boolean): Record<StockPanelTabs, StockFilter[]> => {
  return {
    [StockPanelTabs.PHOTOS]: [...getSeasonalFilter(), ...(IS_GETTY_ENABLED ? [getPricingFilter()] : []), getOrientationFilter()],
    [StockPanelTabs.SHAPES]: [
      {
        type: StockFilterTypes.BUTTON,
        name: StockPanelTabs.SHAPES,
        subFilters: getShapesSubFilters(),
        id: StockButtonFilters.CATEGORY,
      },
    ],
    [StockPanelTabs.STICKERS]: [
      ...getSeasonalFilter(),
      ...(IS_GETTY_ENABLED && !isPremiumUser() ? [getPricingFilter()] : []),
      {type: StockFilterTypes.BUTTON, name: StockButtonFilters.ANIMATION, subFilters: getAnimationSubFilters(), id: StockButtonFilters.ANIMATION},
    ],
    [StockPanelTabs.ICONS]: [{type: StockFilterTypes.BUTTON, name: StockButtonFilters.CATEGORY, subFilters: getIconSubFilters(), id: StockButtonFilters.CATEGORY}],
    [StockPanelTabs.VIDEOS]: [
      ...getSeasonalFilter(),
      ...(IS_GETTY_ENABLED ? [getPricingFilter()] : []),
      {
        type: StockFilterTypes.BUTTON,
        name: window.i18next.t('pmwjs_quality'),
        subFilters: videoQualityFiltersDisabled ? [] : QUALITY_SUBFILTERS,
        id: StockButtonFilters.QUALITY,
      },
      getOrientationFilterForVideos(),
    ],
    // Populated from the backend (posterbuilder/getStockLibraryAudioCategories) via loadAudioCategories in stock-panel.tsx
    [StockPanelTabs.AUDIOS]: [],
    [StockPanelTabs.TEXT_TO_IMAGE]: [],
    [StockPanelTabs.ALL]: [],
  };
};

export const STOCK_SUBFILTER_TO_ADDITIONAL_FILTERS_MAP: Record<StockPanelTabs, Record<string, Record<string, StockFilter[]>>> = {
  [StockPanelTabs.ALL]: {},
  [StockPanelTabs.PHOTOS]: {},
  [StockPanelTabs.SHAPES]: {},
  [StockPanelTabs.STICKERS]: {},
  [StockPanelTabs.ICONS]: {},
  [StockPanelTabs.VIDEOS]: {},
  [StockPanelTabs.AUDIOS]: {},
  [StockPanelTabs.TEXT_TO_IMAGE]: {},
};

export interface StockSubFilterRef {
  filter: StockButtonFilters;
  subFilter: string;
}

export type StockSubFilterExclusivePair = [StockSubFilterRef, StockSubFilterRef];

// Sub-filters whose results can never overlap: selecting one side deselects the other (both stay clickable).
// Getty stickers are stills only, so Getty and Animated are mutually exclusive.
export const MUTUALLY_EXCLUSIVE_SUBFILTERS: Partial<Record<StockPanelTabs, StockSubFilterExclusivePair[]>> = {
  [StockPanelTabs.STICKERS]: [
    [
      {filter: StockButtonFilters.PRICING, subFilter: PricingSubFilter.PREMIUM},
      {filter: StockButtonFilters.ANIMATION, subFilter: AnimationSubFilter.ANIMATED},
    ],
  ],
};

export const AUDIO_CATEGORIES_REQUEST_LOCAL_STORAGE_CACHE_KEY = 'AudioCategoriesRequestCacheKey';
export const DISABLE_PIXABY_SOURCES = true;

export type GridItemBaseStorageBackendData = Record<string, any>;

export interface InternalStockParams {
  stockSources: StockSources[];
  score: number;
  offset: number;
}

export interface ExternalStockParams {
  stockSource: StockSources;
  score: number;
  offset: number;
}

export interface SourceTier {
  internalStock?: InternalStockParams;
  externalStock?: ExternalStockParams[];
}

export interface StockResponse {
  stock: StockBackendData[];
  tierResults: {
    internal: number;
    external: number[];
  }[];
  storyblocksAvailable?: boolean;
  debug?: StockScoreDebugPayload;
}

export type CarouselTabTiers = Partial<Record<StockPanelTabs, SourceTier[]>>;

// scoreDebugPayload is a side-key carrying score debug; it is not a StockPanelTabs key.
export type StockResponseCarousel = Partial<Record<StockPanelTabs, StockBackendData[]>> & {scoreDebugPayload?: StockScoreDebugPayload};

// ===========================================================================================
// Score-debug types powering the engineer-only per-tile hover card (flag-gated, see stock-score-debug.ts).
// ===========================================================================================
export interface StockSearchScoreDebug {
  cleanedKeywords: string;
  topScore: number;
  ratioFloor: number | null;
  cutoff: number | string; // "INF" when the top match was too weak to show anything
  winner: string;
}

export interface StockItemScoreDebug {
  type: string | null;
  keywords: string[] | null;
  description: string | null;
  tokenCount: number;
  queryPath: string;
  finalScore: number | null;
  neuralScore: number | null;
  neuralCosine: number | null;
  neuralFloor: number;
  clearedNeuralFloor: boolean;
  lexicalScore: number | null;
  matchedLexical: boolean;
  search: StockSearchScoreDebug | null;
}

export interface StockScoreDebugPayload {
  items: Record<string, StockItemScoreDebug>;
}

export interface StockScoreDebugAvailabilityResponse {
  isAvailable: boolean;
}

export interface StockGetterParams extends LazyLoadingGetterParams {
  options?: StockGetterOptions;
  isManageappPage?: string;
}